import { useState, useEffect } from "react";
import { AlertCircle, AlertTriangle, CheckCircle2, Loader2, X } from "lucide-react";

export default function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirmation",
  message = "Êtes-vous sûr de vouloir effectuer cette action ?",
  confirmText = "Confirmer",
  cancelText = "Annuler",
  variant = "danger", // "danger" | "primary" | "warning"
  requireReason = false,
  reasonLabel = "Motif de l'action",
  reasonPlaceholder = "Indiquez la raison...",
  isSubmitting = false,
  testId = "confirmation-modal",
}) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setReason("");
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (requireReason && !reason.trim()) {
      setError("Le motif est obligatoire pour valider cette action.");
      return;
    }
    setError(null);
    onConfirm(requireReason ? reason.trim() : undefined);
  };

  const variantStyles = {
    danger: {
      btn: "bg-rose-600 hover:bg-rose-500 text-white",
      icon: <AlertTriangle className="h-5 w-5 text-rose-400 shrink-0" />,
      border: "border-rose-500/30",
    },
    warning: {
      btn: "bg-amber-600 hover:bg-amber-500 text-white",
      icon: <AlertCircle className="h-5 w-5 text-amber-400 shrink-0" />,
      border: "border-amber-500/30",
    },
    primary: {
      btn: "bg-blue-600 hover:bg-blue-500 text-white",
      icon: <CheckCircle2 className="h-5 w-5 text-blue-400 shrink-0" />,
      border: "border-blue-500/30",
    },
  };

  const style = variantStyles[variant] || variantStyles.primary;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-black/70 backdrop-blur-xs flex items-center justify-center p-4"
      data-testid={testId}
    >
      <div className="bg-[#101426] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl p-6 space-y-4 my-8">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5">
            {style.icon}
            <h3 className="text-base font-bold text-white">{title}</h3>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="text-xs text-slate-300 leading-relaxed">{message}</p>

        {requireReason && (
          <div className="space-y-1.5 pt-1">
            <label className="block text-xs font-medium text-slate-300">
              {reasonLabel} <span className="text-rose-400">*</span>
            </label>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={reasonPlaceholder}
              className="w-full rounded-xl bg-slate-900 border border-white/10 p-3 text-xs text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
              data-testid="confirmation-reason-input"
            />
          </div>
        )}

        {error && (
          <div className="p-2.5 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/10">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-xs font-semibold text-slate-400 hover:text-white transition-colors"
            data-testid="confirmation-cancel-btn"
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting}
            className={`px-4 py-2 rounded-xl text-xs font-semibold shadow-md transition-colors flex items-center gap-1.5 ${style.btn}`}
            data-testid="confirmation-confirm-btn"
          >
            {isSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
